import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { IDENTITY_PROVIDERS } from '@24i/nxg-core-utils/src/constants';
import { useUserData } from '@24i/nxg-sdk-smartott-shared/src/context/UserData';
import { log } from '@24i/nxg-core-utils/src/logger';
import { User } from '@24i/nxg-sdk-photon';
import QueryPersistCacheInstance from '@24i/nxg-sdk-smartott-shared/src/context/Query/queryPersistCacheInstance';
import { useSessionId } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useSessionId';
import { useCampaignAnalytics } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useCampaign';
import { useCommonEventParams } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useCommonEventParams';
import { providerLogout, removeUserDataFromStorage, sendLogoutAnalytics } from '../../utils/auth';
import { useAppStart } from '../../context/AppStartContext';
import useUserSession from '../useUserSession';
import { useLogoutNavigation } from '../useLogoutNavigation';

export const useAuth = () => {
    const useSessionContext = useUserSession();
    const queryClient = useQueryClient();
    const userDataClient = useUserData();
    const identityProvider = useFeature('identityProvider');
    const { onLogout } = useAppStart();
    const { navigateAfterLogout } = useLogoutNavigation();
    const provider = identityProvider?.enabled
        ? (identityProvider?.provider as IDENTITY_PROVIDERS)
        : undefined;
    const { sessionId } = useSessionId();
    const { campaignUrl, campaignCookie } = useCampaignAnalytics();
    const { user } = useCommonEventParams();

    const logout = useCallback(
        async (options?: Record<string, unknown>) => {
            try {
                await queryClient.cancelQueries();
                await userDataClient.logout(provider, options);
                await providerLogout(provider);
                onLogout();

                sendLogoutAnalytics(sessionId, campaignUrl, campaignCookie, user as User);
            } catch (e) {
                log(e);
            } finally {
                await removeUserDataFromStorage();

                await useSessionContext.signOutUser();

                await QueryPersistCacheInstance.getInstance()?.getPersistor()?.removeClient();

                queryClient.clear();
                if (!options?.skipNavigation) await navigateAfterLogout(provider);
            }
        },
        [provider, navigateAfterLogout, queryClient, userDataClient, useSessionContext]
    );

    const redirectAfterLogout = useCallback(() => {
        navigateAfterLogout(provider).catch((e) => log(e));
    }, [provider, navigateAfterLogout]);

    return { logout, redirectAfterLogout };
};
